import { isAfter, subDays } from 'date-fns';
import {
  DocumentModuleRtoChecks,
  SignatureDocumentModule,
  SignatureDocumentType,
  DocumentModuleRtoInformation,
} from '../../model';
import { SignatureDocument } from '../../model/signature-area/signature-document';
import {
  AccordoQuadroAfiCodes,
  AccordoQuadroAfiTypes,
  AccordoQuadroSgrCodes,
  AccordoQuadroSgrTypes,
  AdesioneServiziDigitaliCodes,
  AdesioneServiziDigitaliTypes,
  AMLQuestionnaireCodes,
  AMLQuestionnaireTypes,
  ContrattoMyAzimut,
  DIY_ONBOARDING_CODE,
  InvestmentProposalCodes,
  InvestmentProposalTypes,
  MiFiQuestionnaireCodes,
  MiFiQuestionnaireTypes,
  ModuloAperturaRapporti,
  ModuloAperturaRapportiAFI,
  RtoImiOrderCodes,
  RtoImiOrderTypes,
  SchedaFinanziariaTitle,
  SignatureDocumentCodes,
} from '../../model/signature-area/signature-document-type-lists';
import {
  UserRegistrationDocumentCodes,
  UserRegistrationDocumentTypes,
} from '../../model/user-registration/user-registration-document-type';
import { signatureAreaStatus } from '../../utils';
import { DocumentBeanComplexMetadata } from '../generated/papyrus-metadata/api';
import {
  DispositionMetadataBean,
  DocumentNestedGroup,
} from '../generated/papyrus-stream';
import {
  BundleState,
  RecipientState,
} from '../generated/signroom-orchestrator';
import {
  getModuleOperation,
  parseBaseDocument,
  parseModuleInformation,
  parseModuleSignatory,
} from './common-document-parsers';
import { getKidUrl } from '../../utils/api-utils';

const parseGenericModule = (
  item: DocumentBeanComplexMetadata,
  details: BundleState
): any => {
  return {
    ...parseBaseDocument(item, details),
    type: 'GenericModule',
  };
};
const parseDocumentModuleRtoChecks = (
  complexMetadata: DispositionMetadataBean
): DocumentModuleRtoChecks => {
  const order = complexMetadata.orders?.[0];
  return {
    appropriatenessOutcome: order?.notAppropriate
      ? 'Non Appropriato'
      : 'Appropriato',
    complex: '',
    ballIn: false,
    illiquid: false,
    conflictOperation: false,
  };
};

const parseDocumentModuleRtoInformation = (
  complexMetadata: DispositionMetadataBean
): DocumentModuleRtoInformation[] =>
  complexMetadata.orders?.map((order) => ({
    documentName: order.productName,
    isinCode: order.productIsin,
    tool: '',
    owner: complexMetadata.customers[0].name,
    quantity: Number(order.quantity),
    priceLimit: { value: Number(order.amount), currency: order.currency },
    priceMode: order.paymentType,
    validity: '',
    kidUrl:
      order.existsKidDoc === true
        ? getKidUrl(order.productIsin || order.productCode)
        : undefined,
  }));

const parseRtoImiModule = (
  item: DocumentBeanComplexMetadata,
  details: BundleState
): any => {
  const complexMetadata = item.complexMetadata as DispositionMetadataBean;

  return {
    ...parseBaseDocument(item, details),
    type: 'RtoImiModule',
    price: isNaN(Number(complexMetadata.totalAmount))
      ? undefined
      : {
          value: Number(complexMetadata.totalAmount),
          currency: complexMetadata.totalAmountCurrency,
        },
    moduleInformation: parseDocumentModuleRtoInformation(complexMetadata),
    moduleChecks: parseDocumentModuleRtoChecks(complexMetadata),
  };
};

const parseServiceActivationModule = (
  item: DocumentBeanComplexMetadata,
  details: BundleState,
  documents: DocumentBeanComplexMetadata[]
): any => {
  const complexMetadata = item.complexMetadata as DispositionMetadataBean;

  return {
    ...parseBaseDocument(item, details),
    type: 'ServiceActivationModule',
    moduleOperation: getModuleOperation(item, complexMetadata, documents),
    moduleInformation: parseModuleInformation(item, details, complexMetadata),
    moduleSignatory: parseModuleSignatory(details),
  };
};

const parseQuestionnaireModule = (
  item: DocumentBeanComplexMetadata,
  details: BundleState
): any => {
  const complexMetadata = item.complexMetadata as DispositionMetadataBean;
  return {
    ...parseBaseDocument(item, details),
    type: 'QuestionnaireModule',
    moduleInformation: parseModuleInformation(item, details, complexMetadata),
    moduleSignatory: parseModuleSignatory(details),
  };
};

const parseSurveySummaryModuleProspect = (
  item: DocumentBeanComplexMetadata,
  details: BundleState,
  title: string
): any => {
  const complexMetadata = item.complexMetadata as DispositionMetadataBean;
  return {
    ...parseBaseDocument(item, details),
    title: title + '.pdf',
    type: DIY_ONBOARDING_CODE,
    moduleInformation: parseModuleInformation(item, details, complexMetadata),
    moduleSignatory: parseModuleSignatory(details),
  };
};

const parseSignProspectDocuments = (
  item: DocumentBeanComplexMetadata,
  details: BundleState
): any => {
  const complexMetadata = item.complexMetadata as DispositionMetadataBean;
  return {
    ...parseBaseDocument(item, details),
    title: item.documentName ?? 'Documento',
    type: DIY_ONBOARDING_CODE,
    moduleInformation: parseModuleInformation(item, details, complexMetadata),
    moduleSignatory: parseModuleSignatory(details),
  };
};

const parseModuleDOB = (
  bundle: DocumentBeanComplexMetadata,
  details: BundleState
): SignatureDocumentModule => {
  if (
    AccordoQuadroSgrCodes.includes(bundle.documentType as AccordoQuadroSgrTypes)
  ) {
    return parseSurveySummaryModuleProspect(
      bundle,
      details,
      ModuloAperturaRapporti
    );
  }
  if (
    MiFiQuestionnaireCodes.includes(
      bundle.documentType as MiFiQuestionnaireTypes
    )
  ) {
    return parseSurveySummaryModuleProspect(
      bundle,
      details,
      SchedaFinanziariaTitle
    );
  }
  if (
    AdesioneServiziDigitaliCodes.includes(
      bundle.documentType as AdesioneServiziDigitaliTypes
    )
  ) {
    return parseSurveySummaryModuleProspect(bundle, details, ContrattoMyAzimut);
  }

  if (
    AccordoQuadroAfiCodes.includes(bundle.documentType as AccordoQuadroAfiTypes)
  ) {
    return parseSurveySummaryModuleProspect(
      bundle,
      details,
      ModuloAperturaRapportiAFI
    );
  }

  if (bundle.category === DIY_ONBOARDING_CODE) {
    return parseSignProspectDocuments(bundle, details);
  }

  return parseGenericModule(bundle, details);
};

const parseModulePC = (
  bundle: DocumentBeanComplexMetadata,
  details: BundleState,
  documents: DocumentBeanComplexMetadata[]
): SignatureDocumentModule => {
  if (
    AMLQuestionnaireCodes.includes(
      bundle.documentType as AMLQuestionnaireTypes
    ) ||
    MiFiQuestionnaireCodes.includes(
      bundle.documentType as MiFiQuestionnaireTypes
    ) ||
    UserRegistrationDocumentCodes.includes(
      bundle.documentType as UserRegistrationDocumentTypes
    )
  ) {
    return parseQuestionnaireModule(bundle, details);
  }
  if (RtoImiOrderCodes.includes(bundle.documentType as RtoImiOrderTypes)) {
    return parseRtoImiModule(bundle, details);
  }
  if (
    InvestmentProposalCodes.includes(
      bundle.documentType as InvestmentProposalTypes
    )
  ) {
    return parseServiceActivationModule(bundle, details, documents);
  }

  return parseGenericModule(bundle, details);
};

export const parseModule = (
  bundle: DocumentBeanComplexMetadata,
  details: BundleState,
  documents: DocumentBeanComplexMetadata[],
  isProspect: boolean | undefined
): SignatureDocumentModule => {
  if (isProspect) {
    return parseModuleDOB(bundle, details);
  }

  return parseModulePC(bundle, details, documents);
};

export const getSignatureDocumentType = (
  item: DocumentBeanComplexMetadata,
  isProspect?: boolean
): SignatureDocumentType => {
  if (isProspect) {
    return {
      practiceType: 'ONBOARDING_NUOVO_CLIENTE',
    };
  }
  const complexMetadata = item.complexMetadata as DispositionMetadataBean;
  if (complexMetadata?.practiceType === 'MAX') {
    return {
      practiceType: 'MAX',
    };
  }
  if (
    AMLQuestionnaireCodes.includes(item.documentType as AMLQuestionnaireTypes)
  ) {
    return {
      practiceType:
        (item.documentType as AMLQuestionnaireTypes) ===
        'CAMBIO_RECAPITO_SERVIZI_DIGITALI'
          ? 'ContactData'
          : 'AMLQuestionnaire',
    };
  }
  if (
    UserRegistrationDocumentCodes.includes(
      item.documentType as UserRegistrationDocumentTypes
    )
  ) {
    return {
      practiceType: 'DigitalServiceMembership',
    };
  }
  if (
    MiFiQuestionnaireCodes.includes(item.documentType as MiFiQuestionnaireTypes)
  ) {
    return {
      practiceType: 'MiFiDQuestionnaire',
    };
  }
  if (RtoImiOrderCodes.includes(item.documentType as RtoImiOrderTypes)) {
    return {
      practiceType: 'RtoImiOrder',
    };
  }
  if (
    InvestmentProposalCodes.includes(
      item.documentType as InvestmentProposalTypes
    )
  ) {
    return {
      practiceType: 'InvestmentProposal',
      proposalType: 'Base',
    };
  }

  return undefined;
};

const checkMySignatureRequired = (
  customerCode: string,
  customers: RecipientState[]
): boolean => {
  // Trova l'utente corrente
  const currentUser = customers.find(
    (customer) =>
      customer.userId === customerCode && customer.roleTypeCode === 'SIGNER'
  );

  if (!currentUser) {
    return false;
  }

  // Se ha già firmato, non può firmare di nuovo
  if (currentUser.recipientStatus === 'SIGNED') {
    return false;
  }

  // Se non è stato ancora notificato, non può firmare
  if (currentUser.recipientStatus !== 'NOTIFIED_TO_SIGN') {
    return false;
  }

  // Verifica che tutti i firmatari con routingOrder inferiore abbiano già firmato
  const previousSigners = customers.filter(
    (customer) =>
      customer.roleTypeCode === 'SIGNER' &&
      customer.routingOrder < currentUser.routingOrder
  );

  // Se ci sono firmatari precedenti che non hanno ancora firmato, non può firmare
  const allPreviousSigned = previousSigners.every(
    (signer) => signer.recipientStatus === 'SIGNED'
  );

  return allPreviousSigned;
};

const getNextRequiredSignature = (
  customerCode: string,
  customers: RecipientState[]
) => {
  // Trova l'utente corrente
  const currentUser = customers.find(
    (customer) =>
      customer.userId === customerCode && customer.roleTypeCode === 'SIGNER'
  );

  if (!currentUser) {
    return undefined;
  }

  // Trova il prossimo firmatario in base a routingOrder
  const signers = customers
    .filter((c) => c.roleTypeCode === 'SIGNER')
    .sort((a, b) => a.routingOrder - b.routingOrder);

  // Trova il primo che non ha ancora firmato e ha routingOrder >= all'utente corrente
  return signers.find(
    (signer) =>
      signer.recipientStatus !== 'SIGNED' && signer.userId !== customerCode
  );
};

export const filterSignatureDocumentBundle = (bundle: {
  [key: string]: DocumentNestedGroup;
}): DocumentNestedGroup[] =>
  Object.entries(bundle)
    .filter(
      ([key, value]: [string, any]) =>
        value[1]?.length > 0 &&
        !key.includes('mig-feq') &&
        signatureAreaStatus.includes(value[1][0].status)
    )
    .map(([_key, value]) => value[1]);

export const sortDocuments = (a: DocumentBeanComplexMetadata) =>
  a.documentType === 'REPORT_PROPOSTA' ? -1 : 1;

export const inferPracticeType = (
  processTypeCode?: string,
  documentCode?: string,
  isProspect?: boolean
): SignatureDocumentType | undefined => {
  if (isProspect) {
    return {
      practiceType: 'ONBOARDING_NUOVO_CLIENTE',
    };
  }
  if (processTypeCode === 'MAX') {
    return {
      practiceType: 'MAX',
    };
  }
  if (documentCode) {
    if (
      AMLQuestionnaireCodes.includes(documentCode as AMLQuestionnaireTypes)
    ) {
      return {
        practiceType:
          (documentCode as AMLQuestionnaireTypes) ===
          'CAMBIO_RECAPITO_SERVIZI_DIGITALI'
            ? 'ContactData'
            : 'AMLQuestionnaire',
      };
    }
    if (
      UserRegistrationDocumentCodes.includes(
        documentCode as UserRegistrationDocumentTypes
      )
    ) {
      return {
        practiceType: 'DigitalServiceMembership',
      };
    }
    if (
      MiFiQuestionnaireCodes.includes(documentCode as MiFiQuestionnaireTypes)
    ) {
      return {
        practiceType: 'MiFiDQuestionnaire',
      };
    }
    if (RtoImiOrderCodes.includes(documentCode as RtoImiOrderTypes)) {
      return {
        practiceType: 'RtoImiOrder',
      };
    }
    if (
      InvestmentProposalCodes.includes(
        documentCode as InvestmentProposalTypes
      )
    ) {
      return {
        practiceType: 'InvestmentProposal',
        proposalType: 'Base',
      };
    }
  }
  if (processTypeCode) {
    const ptc = processTypeCode.toUpperCase();
    if (ptc.includes('RTO') || ptc.includes('IMI')) {
      return { practiceType: 'RtoImiOrder' };
    }
    if (ptc.includes('MIFID') || ptc.includes('MIFI')) {
      return { practiceType: 'MiFiDQuestionnaire' };
    }
    if (ptc.includes('AML')) {
      return { practiceType: 'AMLQuestionnaire' };
    }
    if (ptc.includes('CONTACT') || ptc.includes('RECAPITO')) {
      return { practiceType: 'ContactData' };
    }
    if (ptc.includes('ONBOARDING')) {
      return { practiceType: 'ONBOARDING_NUOVO_CLIENTE' };
    }
    if (ptc.includes('PROPOSAL') || ptc.includes('PROPOSTA')) {
      return { practiceType: 'InvestmentProposal', proposalType: 'Base' };
    }
  }
  return undefined;
};

export const parseSignatureDocumentBundle = (
  bundles: { documents: DocumentBeanComplexMetadata[]; details: BundleState }[],
  customerCode: string,
  isProspect: boolean,
  processedItems: SignatureDocument[] = []
): SignatureDocument[] => {
  const notReadyItems: SignatureDocument[] = processedItems
    .filter((i) => !bundles?.some((b) => b.details.bundleId === i.id))
    .map((i) => {
      return { ...i, underProcessing: true };
    });

  const readyItems = bundles.map((bundle) => {
    const { details, documents } = bundle;
    const sortedDocuments = [...documents].sort(sortDocuments);
    const modules = sortedDocuments.map((item) =>
      parseModule(item, details, documents, isProspect)
    );

    const firstItem = documents[0] as any;

    const complexMetadata =
      firstItem.complexMetadata as DispositionMetadataBean;

    const customers: RecipientState[] = details.recipients
      ? details.recipients
          .filter((item) => item.userType === 'C')
          .sort((a, b) => a.routingOrder - b.routingOrder)
      : [];

    const isPG = customers.some((c) => !!c.userBehalfOfId);

    const customerName = isPG
      ? firstItem?.ownerName || ''
      : customers.length > 0 ? customers[0].recipientFullName : '';

    const customerNames = isPG
      ? [firstItem?.ownerName].filter(Boolean)
      : customers.map((c) => c.recipientFullName);

    const expirationDate = new Date(details.expirationDate);
    const now = new Date();

    return {
      id: details.bundleId,
      practiceNumber: complexMetadata.planId,
      customerName,
      customerNames,
      isExpiringForMe: isAfter(now, subDays(expirationDate, 1)),
      isExpired: isAfter(now, expirationDate),
      isMySignatureRequired: checkMySignatureRequired(customerCode, customers),
      type: getSignatureDocumentType(documents[0], isProspect),
      modules,
      nextRequiredSignature: getNextRequiredSignature(customerCode, customers)
        ?.recipientFullName,
    };
  });

  return [...notReadyItems, ...readyItems];
};

export const parseBundles = (
  bundles: BundleState[],
  allowedCodes: readonly string[] = SignatureDocumentCodes
): SignatureDocument[] => {
  return bundles
    .filter((b) =>
      b.documents?.some((doc) =>
        allowedCodes.includes(doc.documentCode as any)
      )
    )
    .map((b) => {
      const customerName =
        b?.recipients.length > 0 ? b.recipients[0].recipientFullName : '';

      const expirationDate = new Date(b.expirationDate);
      const now = new Date();

      return {
        id: b.bundleId,
        practiceNumber: '',
        customerName,
        isExpiringForMe: false,
        isExpired: isAfter(now, expirationDate),
        isMySignatureRequired: false,
        underProcessing: false,
        name: b?.processTypeCode.replace('_', ' ') || '',
        totalModules: b?.documents.length || 0,
      };
    });
};
export const parseBundlesToSignatureDocuments = (
  bundles: BundleState[],
  customerCode: string,
  isProspect: boolean
): SignatureDocument[] => {
  const now = new Date();

  return bundles.map((bundle) => {
    const customers: RecipientState[] = bundle.recipients
      ? bundle.recipients
          .filter((item) => item.userType === 'C')
          .sort((a, b) => (a.routingOrder ?? 0) - (b.routingOrder ?? 0))
      : [];

    const isPG = customers.some((c) => !!c.userBehalfOfId);

    const currentUserRecipient = customers.find(
      (c) => c.userId === customerCode && c.roleTypeCode === 'SIGNER'
    );

    const customerName = isPG
      ? currentUserRecipient?.recipientFullName || (customers.length > 0 ? customers[0].recipientFullName : '')
      : customers.length > 0 ? customers[0].recipientFullName : '';

    const customerNames = isPG
      ? [currentUserRecipient?.recipientFullName || ''].filter(Boolean)
      : customers.map((c) => c.recipientFullName).filter(Boolean);

    const expirationDate = bundle.expirationDate ? new Date(bundle.expirationDate) : undefined;
    const isExpired = expirationDate ? isAfter(now, expirationDate) : false;
    const isExpiringForMe = expirationDate ? isAfter(now, subDays(expirationDate, 1)) : false;

    const isMySignatureRequired = checkMySignatureRequired(customerCode, customers);
    const nextRequired = getNextRequiredSignature(customerCode, customers);

    const docCode = bundle.documents?.[0]?.documentCode;
    const practiceType = inferPracticeType(bundle.processTypeCode, docCode, isProspect);

    const name = bundle.processTypeCode?.replace(/_/g, ' ') || bundle.externalId || bundle.bundleId || '';

    return {
      id: bundle.bundleId || '',
      practiceNumber: bundle.externalId || '',
      customerName,
      customerNames,
      isExpiringForMe,
      isExpired,
      isMySignatureRequired,
      nextRequiredSignature: nextRequired?.recipientFullName,
      type: practiceType,
      underProcessing: false,
      name,
      totalModules: bundle.documents?.length || 0,
      modules: undefined,
      rawBundle: bundle,
    };
  });
};
